/**
 * Page layout primitives — every top-level route composes these so width,
 * centering and spacing stay consistent. The visible route title lives in the
 * app-shell breadcrumb; the page-level h1 remains available to assistive tech.
 *
 *   <Page>                          // centered, capped width (max-w-7xl)
 *     <PageHeader>
 *       <PageHeaderTitle data-testid={…}>Render Jobs</PageHeaderTitle>
 *       <PageHeaderDescription>…</PageHeaderDescription>   // optional
 *       <PageHeaderActions><Button …/></PageHeaderActions> // optional
 *     </PageHeader>
 *     <PageContent>…</PageContent>
 *   </Page>
 *
 * The header keeps supporting copy and route actions aligned below the shell.
 */
import * as React from 'react'
import { cn } from '../lib/utils'

/** True inside a <Page>; used to catch accidental <Page> nesting. */
const InPageContext = React.createContext(false)

function Page({ className, ...props }: React.ComponentProps<'div'>) {
  if (React.useContext(InPageContext)) {
    throw new Error(
      '<Page> must not be nested inside another <Page> — a route renders exactly one.',
    )
  }
  return (
    <InPageContext.Provider value={true}>
      <div
        data-slot="page"
        className={cn('mx-auto w-full max-w-7xl space-y-6', className)}
        {...props}
      />
    </InPageContext.Provider>
  )
}

function PageContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="page-content" className={className} {...props} />
}

function PageHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="page-header"
      className={cn(
        'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3',
        className,
      )}
      {...props}
    />
  )
}

function PageHeaderIcon({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="page-header-icon"
      className={cn('sr-only', className)}
      {...props}
    />
  )
}

function PageHeaderTitle({ className, ...props }: React.ComponentProps<'h1'>) {
  return (
    <h1
      data-slot="page-header-title"
      className={cn('sr-only', className)}
      {...props}
    />
  )
}

function PageHeaderDescription({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="page-header-description"
      className={cn(
        'col-start-1 min-w-0 text-sm text-muted-foreground',
        className,
      )}
      {...props}
    />
  )
}

function PageHeaderActions({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="page-header-action"
      className={cn('col-start-2 row-start-1 justify-self-end', className)}
      {...props}
    />
  )
}

export {
  Page,
  PageContent,
  PageHeader,
  PageHeaderIcon,
  PageHeaderTitle,
  PageHeaderDescription,
  PageHeaderActions,
}
